import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useInterval, useUpdateEffect } from 'usehooks-ts';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import Skeleton from 'react-loading-skeleton';
import { uuid } from 'uuidv4';

import './index.less';

import {
  EmbeddingSettings,
  IngestionPipelineOutput,
  IngestionProcessCreationRequest,
  IngestionScheduleModel,
  IngestionSourceCommand,
  IngestionSourceGrade,
  IngestionSourceModelOutput,
  IngestionSourceParametersType,
  LLMSettings,
  ProviderModel,
  SearchPipelineOutput,
} from '../../../../../../gamma-api';
import { defaultApi } from '../../../../../apis';
import { LARGE_PAGE_SIZE, SOURCES_PAGE_SIZE } from '../../../../../constants';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../../../utils/urlUtil';
import { getProjectSourcePath } from '../../../../../utils/pathUtils';
import { AttentionSign, SourcesEmptyLogo } from '../../../../../assets';
import { alertsSelectorAdd, currentFolderIdSelector } from '../../../../../recoil';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';
import IbTypography from '../../../../../components/common/IbTypography';
import IbConfirmModal from '../../../../../components/common/IbConfirmModal';
import SourceTable from '../SourceTable';
import SourceModal from '../SourceModal';
import IbModal from '../../../../../components/common/IbModal';
import ModelSelector from '../../../../../components/ModelSelector';
import ScheduleModal from '../ScheduleModal';

import {
  EMPTY_CONTENT_CLASS_NAME,
  EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME,
  FIELD_CLASS_NAME,
  MAIN_CLASS_NAME,
  NO_MODELS_MODAL_CLASS_NAME,
  NO_MODELS_MODAL_CONTENT_CLASS_NAME,
  NO_MODELS_MODAL_WIDTH,
  NO_PROVIDERS_MODAL_CLASS_NAME,
  NO_PROVIDERS_MODAL_CONTENT_CLASS_NAME,
  REFRESH_INTERVAL,
  SKELETON_CONTAINER_CLASS_NAME,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
} from './const';

export interface ISourcesTabProps {
  projectId: string;
  searchText: string;
  pipelinesLoading: boolean;
  ingestionPipeline?: IngestionPipelineOutput;
  searchPipeline?: SearchPipelineOutput;
  onPipelinesChange?: (
    searchPipeline: SearchPipelineOutput,
    ingestionPipeline: IngestionPipelineOutput
  ) => Promise<boolean>;
  onSourceListChange?: () => Promise<void>;
}

const SourcesTab: React.FC<ISourcesTabProps> = ({
  projectId,
  searchText,
  pipelinesLoading,
  ingestionPipeline,
  searchPipeline,
  onPipelinesChange,
  onSourceListChange,
}) => {
  const { t } = useTranslation();

  const { push } = useHistory();

  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const folderId = useRecoilValue(currentFolderIdSelector);

  const [listLoading, setListLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [providers, setProviders] = useState<ProviderModel[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [noProvidersModalVisible, setNoProvidersModalVisible] = useState(false);

  const [noModelsModalVisible, setNoModelsModalVisible] = useState(false);
  const [localResponseGenerationModel, setLocalResponseGenerationModel] = useState<LLMSettings>();
  const [localEmbeddingModel, setLocalEmbeddingModel] = useState<LLMSettings>();
  const [modelsSaving, setModelsSaving] = useState(false);

  const [sourceModalVisible, setSourceModalVisible] = useState(false);
  const [sourceItemId, setSourceItemId] = useState<string>();
  const [sourceItemType, setSourceItemType] = useState<IngestionSourceParametersType>();
  const [newItemId, setNewItemId] = useState(uuid());

  const [ingestionSourceId, setIngestionSourceId] = useState<string>();
  const [deletionSourceId, setDeletionSourceId] = useState<string>();

  const [schedule, setSchedule] = useState<IngestionScheduleModel>();
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);

  const [sourceList, setSourceList] = useState<IngestionSourceModelOutput[]>([]);

  const [sourcePagination, setSourcePagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: SOURCES_PAGE_SIZE,
    total: 0,
  });

  const sourceListIsEmpty = !listLoading && !sourceList.length;

  const fetchSourceList = async () => {
    try {
      const response = await defaultApi.searchSourcesIngestionSourcesGet(
        undefined,
        undefined,
        [projectId],
        undefined,
        undefined,
        undefined,
        [
          IngestionSourceGrade.Previewing,
          IngestionSourceGrade.Previewed,
          IngestionSourceGrade.Releasing,
          IngestionSourceGrade.Released,
        ],
        searchText,
        (sourcePagination.current || 1) - 1,
        SOURCES_PAGE_SIZE
      );
      setSourceList(response.data.items || []);
      setSourcePagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({ content: t('Failed to load sources'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onInitialLoadAsync = async () => {
    setListLoading(true);
    await fetchSourceList();
    setListLoading(false);
  };

  const onInitialLoadSync = () => {
    onInitialLoadAsync().finally();
  };

  useEffect(onInitialLoadSync, []);

  const onSourcePageOrSearchChange = async () => {
    setPageLoading(true);
    await fetchSourceList();
    setPageLoading(false);
  };

  useEffect(() => {
    onSourcePageOrSearchChange().finally();
  }, [sourcePagination.current, sourcePagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== sourcePagination.current) {
      setSourcePagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (sourcePagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, sourcePagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [sourcePagination.current]);

  useUpdateEffect(() => {
    if (sourcePagination.current !== 1) {
      setSourcePagination({ ...sourcePagination, current: 1 });
    }
  }, [searchText]);

  const onRefreshLoadAsync = async () => {
    await fetchSourceList();
  };

  const onRefreshLoadSync = () => {
    onRefreshLoadAsync().finally();
  };

  useInterval(onRefreshLoadSync, REFRESH_INTERVAL);

  useEffect(() => {
    setLocalResponseGenerationModel(searchPipeline?.llm);
  }, [searchPipeline?.llm]);

  useEffect(() => {
    setLocalEmbeddingModel(ingestionPipeline?.embedding);
  }, [ingestionPipeline?.embedding]);

  const creationButtonLoading = pipelinesLoading || providersLoading;

  const showCreateModal = () => {
    setSourceModalVisible(true);
    setNewItemId(uuid());
    setSourceItemId(undefined);
    setSourceItemType(undefined);
  };

  const onCreationClick = async () => {
    if (creationButtonLoading) {
      return;
    }

    try {
      setProvidersLoading(true);
      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true, true);
      const providers = response.data.items || [];
      setProviders(providers);

      if (!providers.length) {
        setNoProvidersModalVisible(true);
        setProvidersLoading(false);
        return;
      }
    } catch (err) {
      addAlert({ content: t('Providers: Failed to load providers'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setProvidersLoading(false);
    }

    if (!searchPipeline?.llm?.model_name || !ingestionPipeline?.embedding?.model_name) {
      setLocalResponseGenerationModel(searchPipeline?.llm);
      setLocalEmbeddingModel(ingestionPipeline?.embedding);
      setNoModelsModalVisible(true);
      return;
    }

    showCreateModal();
  };

  const onSourceOpen = async (selectedSource: IngestionSourceModelOutput) => {
    push(getProjectSourcePath(folderId, selectedSource.project_id, selectedSource.source_id));
  };

  const onSourceSetup = (selectedSource: IngestionSourceModelOutput) => {
    setSourceItemId(selectedSource.source_id);
    setSourceItemType(selectedSource.versions.draft.source.parameters.type);
    setSourceModalVisible(true);
  };

  const onSourceClose = () => {
    setSourceModalVisible(false);
    setSourceItemId(undefined);
    setSourceItemType(undefined);
  };

  const onSourceIngest = (selectedSource: IngestionSourceModelOutput) => {
    setIngestionSourceId(selectedSource.source_id);
  };

  const onSourceDelete = (selectedSource: IngestionSourceModelOutput) => {
    setDeletionSourceId(selectedSource.source_id);
  };

  const onSourceSchedule = (selectedSource: IngestionSourceModelOutput) => {
    setSchedule(
      selectedSource.schedule ?? {
        schedule_id: '',
        source_id: selectedSource.source_id,
        settings: {
          enabled: false,
        },
      }
    );
    setScheduleModalVisible(true);
  };

  const onProcessRestart = async (selectedSource?: IngestionSourceModelOutput) => {
    const sourceId = selectedSource?.source_id;
    if (!sourceId) {
      return;
    }

    setItemLoading(true);

    const processRequest: IngestionProcessCreationRequest = {
      project_id: projectId,
      brief_instructions: [
        {
          source_id: sourceId,
          command: IngestionSourceCommand.Restart,
          action: null,
          stages: {},
        },
      ],
    };

    try {
      await defaultApi.createProcessIngestionProcessesPost(processRequest);
    } catch (err) {
      addAlert({ content: t('Failed to restart source ingestion'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      setItemLoading(false);
      return;
    }

    await fetchSourceList();

    setItemLoading(false);
  };

  const onSourcePaginationChange = (page: number) => setSourcePagination({ ...sourcePagination, current: page });

  const onSourceItemChange = async () => {
    setListLoading(true);
    await fetchSourceList();
    await onSourceListChange?.();
    setListLoading(false);
  };

  const renderSourceModal = () => {
    return (
      <SourceModal
        key={sourceItemId ? sourceItemId : newItemId}
        pipeline={ingestionPipeline}
        projectId={projectId}
        sourceId={sourceItemId}
        sourceType={sourceItemType}
        visible={sourceModalVisible}
        onChange={onSourceItemChange}
        onClose={onSourceClose}
      />
    );
  };

  const renderScheduleModal = () => {
    if (!schedule) return null;

    const onScheduleSave = async (model: IngestionScheduleModel) => {
      try {
        if (model.schedule_id) {
          await defaultApi.updateSchedule(model.schedule_id, model);
        } else {
          await defaultApi.createSchedule(model);
        }
        setScheduleModalVisible(false);
        setSchedule(undefined);
        await onRefreshLoadAsync();
      } catch (e) {
        addAlert({ content: t('Schedule modal: Save error'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    const onScheduleClose = () => {
      setScheduleModalVisible(false);
      setSchedule(undefined);
    };

    return (
      <ScheduleModal
        key={schedule.source_id}
        schedule={schedule}
        visible={scheduleModalVisible}
        onClose={onScheduleClose}
        onSave={onScheduleSave}
      />
    );
  };

  const renderIngestionConfirmModal = () => {
    const onConfirmClick = async () => {
      if (!ingestionSourceId) {
        return;
      }

      setItemLoading(true);

      const processRequest: IngestionProcessCreationRequest = {
        project_id: projectId,
        brief_instructions: [
          {
            source_id: ingestionSourceId,
            command: IngestionSourceCommand.Ingest,
            action: null,
            stages: {},
          },
        ],
      };

      try {
        await defaultApi.createProcessIngestionProcessesPost(processRequest);
      } catch (err) {
        addAlert({ content: t('Failed to start source deletion'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setItemLoading(false);
        return;
      }

      await fetchSourceList();

      setItemLoading(false);
      setIngestionSourceId(undefined);
    };

    const onCancelClick = () => {
      setIngestionSourceId(undefined);
    };

    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="warning" onClick={onConfirmClick}>
              {t('Ingest')}
            </IbButton>
            <IbButton key="delete-cancel" status="warning" type="secondary" onClick={onCancelClick}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={itemLoading}
        title={t('Ingest source?')}
        visible={!!ingestionSourceId}
        onCancel={onCancelClick}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Are you sure you want to ingest the source and its content?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  const renderDeletionConfirmModal = () => {
    const onConfirmClick = async () => {
      if (!deletionSourceId) {
        return;
      }

      setItemLoading(true);

      const processRequest: IngestionProcessCreationRequest = {
        project_id: projectId,
        brief_instructions: [
          {
            source_id: deletionSourceId,
            command: IngestionSourceCommand.Delete,
            action: null,
            stages: {},
          },
        ],
      };

      try {
        await defaultApi.createProcessIngestionProcessesPost(processRequest);
      } catch (err) {
        addAlert({ content: t('Failed to start source deletion'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setItemLoading(false);
        return;
      }

      await fetchSourceList();

      setItemLoading(false);
      setDeletionSourceId(undefined);
    };

    const onCancelClick = () => {
      setDeletionSourceId(undefined);
    };

    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onConfirmClick}>
              {t('Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onCancelClick}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={itemLoading}
        title={t('Delete source?')}
        visible={!!deletionSourceId}
        onCancel={onCancelClick}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Are you sure you want to remove the source and its content?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  const renderNoProvidersModal = () => {
    const onNoProvidersModalCancel = () => setNoProvidersModalVisible(false);

    return (
      <IbModal
        className={NO_PROVIDERS_MODAL_CLASS_NAME}
        visible={noProvidersModalVisible}
        onCancel={onNoProvidersModalCancel}
      >
        <div className={NO_PROVIDERS_MODAL_CONTENT_CLASS_NAME}>
          <AttentionSign />
          <IbTypography>
            <h2>{t('Sources tab: No providers modal: Title')}</h2>
          </IbTypography>
          <IbTypography.Paragraph>
            <Trans
              components={[<Link key={0} to="/providers" />]}
              i18nKey="Sources tab: No providers modal: Text"
              t={t}
            />
          </IbTypography.Paragraph>
        </div>
      </IbModal>
    );
  };

  const renderNoModelsModal = () => {
    const onNoModelsModalCancel = () => setNoModelsModalVisible(false);

    const onLLMChange = (llm: LLMSettings) => {
      setLocalResponseGenerationModel(llm);
    };

    const onEmbeddingChange = (settings: EmbeddingSettings) => {
      setLocalEmbeddingModel(settings);
    };

    const onModelsSave = async () => {
      setModelsSaving(true);

      const pipelinesSaved = await onPipelinesChange?.(
        { ...searchPipeline, llm: localResponseGenerationModel },
        {
          ...ingestionPipeline,
          embedding: localEmbeddingModel,
        }
      );

      setModelsSaving(false);

      if (!pipelinesSaved) {
        return;
      }

      setNoModelsModalVisible(false);

      showCreateModal();
    };

    const saveDisabled = !localEmbeddingModel?.model_name || !localResponseGenerationModel?.model_name;

    return (
      <IbModal
        className={NO_MODELS_MODAL_CLASS_NAME}
        footer={
          <>
            <IbButton disabled={saveDisabled} onClick={onModelsSave}>
              {t('Save')}
            </IbButton>
            <IbButton type="secondary" onClick={onNoModelsModalCancel}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={modelsSaving}
        title={t('Sources tab: No models modal: Title')}
        visible={noModelsModalVisible}
        width={NO_MODELS_MODAL_WIDTH}
        onCancel={onNoModelsModalCancel}
      >
        <div className={NO_MODELS_MODAL_CONTENT_CLASS_NAME}>
          <div className={FIELD_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Sources tab: No models modal: Response generation LLM model: Title')}
            </IbTypography.Paragraph>
            <ModelSelector.LLM
              loading={providersLoading}
              providers={providers}
              settings={localResponseGenerationModel}
              showAdditionalSettings={false}
              onChange={onLLMChange}
            />
          </div>
          <div className={FIELD_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Sources tab: No models modal: Embedding model: Title')}
            </IbTypography.Paragraph>
            <ModelSelector.Embedding
              loading={providersLoading}
              providers={providers}
              settings={localEmbeddingModel}
              onChange={onEmbeddingChange}
            />
          </div>
        </div>
      </IbModal>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {sourceListIsEmpty ? (
        <div className={EMPTY_CONTENT_CLASS_NAME}>
          <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
            <SourcesEmptyLogo />
            <IbTypography.Paragraph disabled type="secondary">
              {t('Sources tab: Empty source list text')}
            </IbTypography.Paragraph>
            <IbButton icon={<IbIcon iconName="add-one" />} loading={creationButtonLoading} onClick={onCreationClick}>
              {t('Sources tab: Create')}
            </IbButton>
          </div>
        </div>
      ) : (
        <>
          {listLoading && (
            <div className={SKELETON_CONTAINER_CLASS_NAME}>
              <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
            </div>
          )}
          {!listLoading && (
            <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreationClick}>
              {t('Sources tab: Create')}
            </IbButton>
          )}
          <SourceTable
            dataLoading={listLoading}
            pageLoading={pageLoading}
            pagination={sourcePagination}
            searchText={searchText}
            sources={sourceList}
            onDelete={onSourceDelete}
            onIngest={onSourceIngest}
            onOpen={onSourceOpen}
            onPaginationChange={onSourcePaginationChange}
            onRestart={onProcessRestart}
            onSchedule={onSourceSchedule}
            onSetup={onSourceSetup}
          />
        </>
      )}
      {renderSourceModal()}
      {renderIngestionConfirmModal()}
      {renderDeletionConfirmModal()}
      {renderNoProvidersModal()}
      {renderNoModelsModal()}
      {renderScheduleModal()}
    </div>
  );
};

export default SourcesTab;
